import { useCallback, useState } from 'react'

import { type BadgeValueType } from './types'

export function useBadgeValues<T> ({
  defaultProp,
  onAdd: onAddProp,
  onRemove: onRemoveProp,
  prop,
}: {
  defaultProp: string[];
  onAdd?: (value: string) => boolean | void;
  onRemove?: (badge: BadgeValueType<T>, index: number) => boolean | void;
  prop?: T;
}) {
  const isUncontrolled = prop === undefined
  const [uncontrolledBadgeValues, setUncontrolledBadgeValues] =
    useState(defaultProp)
  const badgeValues = (
    isUncontrolled ? uncontrolledBadgeValues : prop ?? []
  ) as BadgeValueType<T>[]

  const onAdd = useCallback(
    (value: string) => {
      const followDefaultBehavior = onAddProp?.(value) ?? true

      if (isUncontrolled && followDefaultBehavior) {
        setUncontrolledBadgeValues((prevBadgeValues) => [
          ...prevBadgeValues,
          value,
        ])
      }

      return followDefaultBehavior
    },
    [isUncontrolled, onAddProp]
  )

  const onRemove = useCallback(
    (badgeValue: BadgeValueType<T>, index: number) => {
      const followDefaultBehavior = onRemoveProp?.(badgeValue, index) ?? true

      if (isUncontrolled && followDefaultBehavior) {
        setUncontrolledBadgeValues((prevBadgeValues) =>
          prevBadgeValues.filter((_, i) => i !== index)
        )
      }

      return followDefaultBehavior
    },
    [isUncontrolled, onRemoveProp]
  )

  return {
    onAdd,
    onRemove,
    badgeValues,
  }
}
